import React, {
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { FormInstance, Schema, Text } from 'rsuite';
import {
  PresentationGrid,
  PresentationRow,
  PresentationCol,
} from '../../layout/PresentationLayout';
import { FormModelPerson, Person } from 'src/types/person';
import {
  apiPersonToFormModel,
  getInitialFormModelValues,
  mapFormModelToPerson,
} from 'src/common/people';
import { useSelector } from 'react-redux';
import { selectConfigExpressRegistrantOptions } from 'src/store/slices/config/configSelectors';
import { ApplicationData, REGISTRANT_OPTIONS } from 'src/types/flow';
import {
  HOA_OPTIONS,
  LOCAL_CONTACT,
  PROPERTY_MANAGEMENT_OPTIONS,
} from 'src/components/payment/constants';
import { useUserRole } from 'src/hooks/useUserRole';
import PersonDetails from '../PersonDetails';
import { formValuePersonSchema } from '../Owners/utils';
import {
  emailRequiredValidation,
  uniqueEmailValidation,
} from 'src/common/validation';
import { HoaRadioGroup } from './HoaRadioGroup';
import { LocalContactRadioGroup } from './LocalContactRadioGroup';
import { PropertyManagerRadioGroup } from './PropertyManagerRadioGroup';
import {
  getHoaValidationModel,
  getOwnerRepValidationModel,
  getPropertyManagerValidationModel,
  SELECTED_CONTACT_PROXIMITY_MESSAGE,
} from './validations';
import {
  ExpressRegistrationRegistrant,
  PersonOptionalFields,
} from 'src/types/configuration';
import { FLOWS_ENUM } from 'src/common/enums';
import { lookupAddress } from 'src/api/apiAccess';
import { LookupAddressResponse } from 'src/types/address';
import { CustomHTML } from 'src/components/common/CustomHTML';

interface ContactPersonData {
  description: string;
  enabled: boolean;
  optionalFields: PersonOptionalFields;
  verbiage?: string;
}

interface ContactFormProps {
  data: ApplicationData;
  flow: FLOWS_ENUM;
  hoaData: ContactPersonData;
  localContactData: ContactPersonData;
  propertyManagerData: ContactPersonData;
}

export interface ContactFormRef {
  getValues: () => ApplicationData;
  validateForm: () => Promise<boolean>;
}

export const ContactForm = forwardRef<ContactFormRef, ContactFormProps>(
  (
    {
      data,
      hoaData,
      localContactData,
      propertyManagerData,
      flow,
    }: ContactFormProps,
    ref
  ) => {
    const isAdminUpdate = flow === FLOWS_ENUM.ADMIN_UPDATE;
    const isApply = flow === FLOWS_ENUM.APPLY_LICENSE;
    const propertyManagerForm = useRef<FormInstance<FormModelPerson>>(null);
    const ownerRepForm = useRef<FormInstance<FormModelPerson>>(null);
    const hoaForm = useRef<FormInstance<FormModelPerson>>(null);
    const [propertyManagerFormValues, setPropertyManagerFormValues] =
      useState<FormModelPerson>(getInitialFormModelValues());
    const [ownerRepFormValues, setOwnerRepFormValues] =
      useState<FormModelPerson>(getInitialFormModelValues());
    const [hoaFormValues, setHoaFormValues] = useState<FormModelPerson>(
      getInitialFormModelValues()
    );
    const [localContactError, setLocalContactError] = useState<string>();
    const [hoaError, setHoaError] = useState<string>();
    const [physicalAddressProximityError, setPhysicalAddressProximityError] =
      useState<string | undefined>();
    const [selectedContact, setSelectedContact] = useState(LOCAL_CONTACT.OWNER);
    const [selectedManager, setSelectedManager] = useState(
      PROPERTY_MANAGEMENT_OPTIONS.SELF_MANAGED
    );
    const [selectedHoa, setSelectedHoa] = useState<HOA_OPTIONS | undefined>(
      undefined
    );

    const handleChangeHoa = useCallback((value: HOA_OPTIONS) => {
      setSelectedHoa(value);
      setHoaError(undefined);
      if (value === HOA_OPTIONS.NO) {
        setHoaFormValues(getInitialFormModelValues());
      }
    }, []);

    const handleChangeManager = useCallback(
      (value: PROPERTY_MANAGEMENT_OPTIONS) => {
        setSelectedManager(value);
        if (
          value === PROPERTY_MANAGEMENT_OPTIONS.SELF_MANAGED &&
          selectedContact === LOCAL_CONTACT.PROPERTY_MANAGER
        ) {
          setSelectedContact(LOCAL_CONTACT.OWNER);
        }
      },
      [selectedContact]
    );

    const handleChangeContact = useCallback((value: LOCAL_CONTACT) => {
      setSelectedContact(value);
      setLocalContactError(undefined);
      setPhysicalAddressProximityError(undefined);
    }, []);

    const { contacts, registrant, property, tenant } = data;

    const selectedRegistrantOptions = useSelector(
      selectConfigExpressRegistrantOptions
    );
    const {
      isPropertyManager: isPropertyManagerByEmail,
      isOwner,
      isRegistrant,
      isRegistrantTenant,
    } = useUserRole(registrant, contacts, tenant);

    // When contacts is already populated, useUserRole's isPropertyManager checks
    // contacts.propertyManager.email — which is empty until the PM form is filled.
    // Fall back to registrantType so the MYSELF_PM_LABEL option stays visible on revisit.
    // Guard against stale contacts.propertyManager.email when the user switches from PM to Tenant:
    // registrantType is authoritative, so if it says TENANT the user is never a PM.
    const isPropertyManager = useMemo(
      () =>
        registrant?.registrantType !== REGISTRANT_OPTIONS.TENANT &&
        (isPropertyManagerByEmail ||
          registrant?.registrantType === REGISTRANT_OPTIONS.PROPERTY_MANAGER ||
          registrant?.registrantType ===
            REGISTRANT_OPTIONS.PROPERTY_MANAGER_INDEPENDENT),
      [isPropertyManagerByEmail, registrant?.registrantType]
    );

    // useUserRole.isOwner checks only email membership in the owner array, which can be
    // stale when the user switches from OWNER to PROPERTY_MANAGER in the same session
    // (their email carries over into owner[]). When registrantType explicitly says PM,
    // the user is not acting as an owner and isOwner must be false.
    const isOwnerEffective = useMemo(
      () =>
        Boolean(isOwner) &&
        registrant?.registrantType !== REGISTRANT_OPTIONS.PROPERTY_MANAGER &&
        registrant?.registrantType !==
          REGISTRANT_OPTIONS.PROPERTY_MANAGER_INDEPENDENT &&
        registrant?.registrantType !== REGISTRANT_OPTIONS.TENANT,
      [isOwner, registrant?.registrantType]
    );

    const getPropertyManager = useCallback((): Person => {
      // THe property manager here comes from the previous page? What if we select the property manager from this one?

      if (selectedManager === PROPERTY_MANAGEMENT_OPTIONS.SELF_MANAGED) {
        if (isOwnerEffective) {
          return { ...(registrant?.owner[0] as Person) };
        }
        if (
          data.registrant?.registrantType ===
            REGISTRANT_OPTIONS.PROPERTY_MANAGER_INDEPENDENT ||
          data.registrant?.registrantType ===
            REGISTRANT_OPTIONS.PROPERTY_MANAGER
        ) {
          return data.registrant.registrant;
        }

        return { ...(contacts?.propertyManager as Person) };
      } else if (selectedManager === PROPERTY_MANAGEMENT_OPTIONS.TENANT) {
        return { ...(tenant as Person) };
      }
      return { ...propertyManagerFormValues };
    }, [
      contacts,
      data,
      isOwnerEffective,
      propertyManagerFormValues,
      registrant,
      selectedManager,
      tenant,
    ]);

    useImperativeHandle(ref, () => ({
      validateForm: async () => {
        if (
          selectedContact === LOCAL_CONTACT.OTHER &&
          !ownerRepForm.current?.check()
        ) {
          console.error('Local Contact Form is invalid');
          return false;
        } else if (physicalAddressRequired) {
          const allowedZipCodes: number[] =
            localContactData.optionalFields.physical_address
              ?.allowed_postcodes ?? [];
          let selectedPerson: FormModelPerson | undefined;
          if (selectedContact === LOCAL_CONTACT.OWNER) {
            selectedPerson = registrant?.owner?.[0] as Person | undefined;
          } else if (selectedContact === LOCAL_CONTACT.PROPERTY_MANAGER) {
            selectedPerson = getPropertyManager();
          }
          if (
            selectedContact !== LOCAL_CONTACT.OTHER &&
            allowedZipCodes.length > 0
          ) {
            let zipcode =
              selectedPerson?.completePhysicalAddress?.components?.zipcode;
            if (
              zipcode === undefined &&
              selectedContact === LOCAL_CONTACT.OWNER
            ) {
              const mailingAddress = selectedPerson?.mailingAddress;
              if (mailingAddress) {
                const { data } = (await lookupAddress(mailingAddress)) as {
                  data: LookupAddressResponse;
                };
                zipcode = data?.suggestions?.[0]?.zipcode;
              }
            }
            if (
              zipcode === undefined ||
              !allowedZipCodes.includes(Number(zipcode))
            ) {
              setPhysicalAddressProximityError(
                SELECTED_CONTACT_PROXIMITY_MESSAGE
              );
              return false;
            }
          }
          setPhysicalAddressProximityError(undefined);
        }

        if (
          selectedManager === PROPERTY_MANAGEMENT_OPTIONS.SELF_MANAGED &&
          selectedContact === LOCAL_CONTACT.PROPERTY_MANAGER &&
          !isPropertyManager
        ) {
          setLocalContactError('Please select a local contact.');
          return false;
        }

        if (showPropertyManagerQuestion) {
          // Only validate the PM PersonDetails form when it is actually rendered
          // (mirrors the render condition at line 483: !isPropertyManager || isOwnerEffective).
          if (!isPropertyManager || isOwnerEffective) {
            if (
              selectedManager ===
                PROPERTY_MANAGEMENT_OPTIONS.PROPERTY_MANAGER &&
              !propertyManagerForm.current?.check()
            ) {
              console.error(
                'Property Manager Form is invalid',
                selectedManager
              );
              return false;
            }
          }
        }
        if (hoaData.enabled) {
          if (!selectedHoa) {
            setHoaError('Please select an answer.');
            return false;
          }
          if (selectedHoa === HOA_OPTIONS.YES && !hoaForm.current?.check()) {
            console.error('HOA Contact Form is invalid');
            return false;
          }
        }

        return true;
      },
      getValues: (): ApplicationData => {
        const identifiedPropertyManager: Person = getPropertyManager();
        let localContact: FormModelPerson;

        switch (selectedContact) {
          case LOCAL_CONTACT.PROPERTY_MANAGER:
            localContact = { ...identifiedPropertyManager };
            break;
          case LOCAL_CONTACT.TENANT:
            localContact = {
              ...((tenant ?? registrant?.registrant) as Person),
            };
            break;
          case LOCAL_CONTACT.OWNER:
          default:
            localContact = { ...(registrant?.owner?.[0] as Person) };
            break;
          case LOCAL_CONTACT.OTHER:
            localContact = { ...ownerRepFormValues };
            break;
        }

        return {
          contacts: {
            selectedManagementOption: selectedManager,
            selectedOwnerRepOption: selectedContact,
            propertyManager: mapFormModelToPerson(identifiedPropertyManager),
            localContact: mapFormModelToPerson(localContact),
            hoaAnswer: hoaData.enabled ? selectedHoa : undefined,
            hoaContact:
              hoaData.enabled && selectedHoa === HOA_OPTIONS.YES
                ? mapFormModelToPerson(hoaFormValues)
                : undefined,
          },
        } as ApplicationData;
      },
    }));

    const getSameAsPropertyAddress = useCallback(
      (person: Person) => {
        return Boolean(
          person.mailingAddress &&
            person.mailingAddress.toLowerCase() ===
              property?.propertyaddress.toLowerCase()
        );
      },
      [property]
    );

    useEffect(() => {
      // Initial Values of the Form
      if (contacts !== undefined) {
        // Management Radio Option
        setSelectedManager(
          (prev) => contacts?.selectedManagementOption ?? prev
        );
        const savedContact = contacts?.selectedOwnerRepOption;
        const defaultContact = isRegistrantTenant
          ? LOCAL_CONTACT.TENANT
          : isPropertyManager
            ? LOCAL_CONTACT.PROPERTY_MANAGER
            : LOCAL_CONTACT.OWNER;
        const isStaleSelfOption =
          (savedContact === LOCAL_CONTACT.PROPERTY_MANAGER &&
            !isPropertyManager) ||
          (savedContact === LOCAL_CONTACT.TENANT && !isRegistrantTenant);
        setSelectedContact(
          isStaleSelfOption ? defaultContact : savedContact ?? defaultContact
        );

        // Values of Local Contact if Selection is OTHER
        if (contacts.selectedOwnerRepOption === LOCAL_CONTACT.OTHER) {
          const person = contacts.localContact;
          if (person) {
            const formPerson = !isApply ? apiPersonToFormModel(person) : person;
            setOwnerRepFormValues({
              ...formPerson,
              sameAsPropertyAddress: getSameAsPropertyAddress(formPerson),
            });
          }
        }
        // Values of Property Manager if Selection is PROPERTY_MANAGER
        if (
          contacts.selectedManagementOption ===
          PROPERTY_MANAGEMENT_OPTIONS.PROPERTY_MANAGER
        ) {
          const person = contacts.propertyManager;
          if (person) {
            const formPerson = !isApply ? apiPersonToFormModel(person) : person;
            setPropertyManagerFormValues({
              ...formPerson,
              sameAsPropertyAddress: getSameAsPropertyAddress(formPerson),
            });
          }
        }

        // HOA Answer and Contact Details
        setSelectedHoa(contacts.hoaAnswer);
        if (contacts.hoaAnswer === HOA_OPTIONS.YES) {
          const person = contacts.hoaContact;
          if (person) {
            const formPerson = !isApply ? apiPersonToFormModel(person) : person;
            setHoaFormValues({
              ...formPerson,
              sameAsPropertyAddress: getSameAsPropertyAddress(formPerson),
            });
          }
        }
      } else {
        // Set defaults for first time form fill
        if (isPropertyManager) {
          setSelectedContact(LOCAL_CONTACT.PROPERTY_MANAGER);
        } else if (isRegistrantTenant) {
          setSelectedContact(LOCAL_CONTACT.TENANT);
        } else {
          setSelectedContact(LOCAL_CONTACT.OWNER);
        }
      }
    }, [
      contacts,
      isPropertyManager,
      isRegistrantTenant,
      getSameAsPropertyAddress,
    ]);

    // When registrantType is not yet set (e.g. first visit in APPLY flow before
    // completing the registrant step), treat as unconstrained: show the section,
    // allow changes, and show "Myself" in the local contact list. Once registrant
    // data exists the normal isOwner / isRegistrant guards apply.
    const registrantNotYetSet = useMemo(
      () => !registrant?.registrantType,
      [registrant]
    );

    const showPropertyManagerQuestion = useMemo(() => {
      if (flow === FLOWS_ENUM.APPLY_LICENSE) {
        return (
          propertyManagerData.enabled &&
          (!registrant?.registrantType ||
            registrant?.registrantType === REGISTRANT_OPTIONS.OWNER ||
            registrant?.registrantType === REGISTRANT_OPTIONS.TENANT)
        );
      }
      if (flow === FLOWS_ENUM.ADMIN_UPDATE) {
        return (
          propertyManagerData.enabled &&
          registrant?.registrantType !== REGISTRANT_OPTIONS.PROPERTY_MANAGER &&
          registrant?.registrantType !==
            REGISTRANT_OPTIONS.PROPERTY_MANAGER_INDEPENDENT
        );
      }
      return (
        propertyManagerData.enabled &&
        registrant?.registrantType !== REGISTRANT_OPTIONS.PROPERTY_MANAGER &&
        registrant?.registrantType !==
          REGISTRANT_OPTIONS.PROPERTY_MANAGER_INDEPENDENT
      );
    }, [flow, propertyManagerData, registrant]);

    const canChangePropertyManager = useMemo(() => {
      return (
        registrantNotYetSet ||
        (isOwnerEffective && isRegistrant) ||
        isRegistrantTenant ||
        isAdminUpdate
      );
    }, [
      isRegistrant,
      isOwnerEffective,
      isAdminUpdate,
      registrantNotYetSet,
      isRegistrantTenant,
    ]);

    const canChangePropertyManagerDetails = useMemo(() => {
      return (
        registrantNotYetSet ||
        isRegistrant ||
        isAdminUpdate ||
        isRegistrantTenant
      );
    }, [isRegistrant, isAdminUpdate, registrantNotYetSet, isRegistrantTenant]);

    const pmExcludedEmails = useMemo(() => {
      const emails: string[] = (registrant?.owner ?? []).map(
        (o) => (o as Person).email!
      );
      if (registrant?.registrant?.email) {
        emails.push(registrant.registrant.email);
      }
      return emails;
    }, [registrant]);

    const localContactExcludedEmails = useMemo(() => {
      const extra: string[] = [];
      if (selectedManager === PROPERTY_MANAGEMENT_OPTIONS.PROPERTY_MANAGER) {
        if (propertyManagerFormValues.email) {
          extra.push(propertyManagerFormValues.email);
        }
      } else if (selectedManager === PROPERTY_MANAGEMENT_OPTIONS.TENANT) {
        if ((tenant as Person)?.email) {
          extra.push((tenant as Person).email!);
        }
      }
      return [...pmExcludedEmails, ...extra];
    }, [
      pmExcludedEmails,
      selectedManager,
      propertyManagerFormValues.email,
      tenant,
    ]);

    const propertyManagerFormModel = useMemo(() => {
      const emailRule = pmExcludedEmails.length
        ? uniqueEmailValidation(pmExcludedEmails)
        : emailRequiredValidation;
      return Schema.Model<FormModelPerson>({
        ...formValuePersonSchema,
        email: emailRule,
      });
    }, [pmExcludedEmails]);

    const propertyManagerDynamicValidationModel = useMemo(() => {
      const base = getPropertyManagerValidationModel(
        propertyManagerData.optionalFields
      );
      const emailRule = pmExcludedEmails.length
        ? uniqueEmailValidation(pmExcludedEmails)
        : base.email;
      return { ...base, email: emailRule };
    }, [pmExcludedEmails, propertyManagerData.optionalFields]);

    const physicalAddressRequired =
      !!localContactData.optionalFields.physical_address?.required;

    const ownerRepFormModel = useMemo(() => {
      const emailRule = localContactExcludedEmails.length
        ? uniqueEmailValidation(localContactExcludedEmails)
        : emailRequiredValidation;
      return Schema.Model<FormModelPerson>({
        ...formValuePersonSchema,
        email: emailRule,
      });
    }, [localContactExcludedEmails]);

    const ownerRepDynamicValidationModel = useMemo(() => {
      const base = getOwnerRepValidationModel(localContactData.optionalFields);
      const emailRule = localContactExcludedEmails.length
        ? uniqueEmailValidation(localContactExcludedEmails)
        : base.email;
      return { ...base, email: emailRule };
    }, [localContactExcludedEmails, localContactData.optionalFields]);

    const hoaFormModel = useMemo(() => {
      return Schema.Model<FormModelPerson>({
        ...formValuePersonSchema,
        email: emailRequiredValidation,
      });
    }, []);

    const hoaDynamicValidationModel = useMemo(() => {
      return getHoaValidationModel(hoaData.optionalFields);
    }, [hoaData.optionalFields]);

    return (
      <PresentationGrid
        className="mt-12 ml-0 "
        fluid
      >
        {showPropertyManagerQuestion && (
          <>
            <PresentationRow className="mb-16">
              <PresentationCol xs={24}>
                <Text
                  as="h5"
                  className="text-bold mb-8"
                >
                  Property Management Company
                </Text>
                <CustomHTML text={propertyManagerData.description} />
              </PresentationCol>
            </PresentationRow>
            <PresentationRow>
              <PresentationCol
                md={12}
                xs={24}
              >
                <PropertyManagerRadioGroup
                  configuration={
                    selectedRegistrantOptions as ExpressRegistrationRegistrant
                  }
                  disabled={!canChangePropertyManager}
                  isAdminUpdate={isAdminUpdate}
                  isOwner={isOwnerEffective}
                  isPropertyManager={isPropertyManager}
                  onChange={handleChangeManager}
                  value={selectedManager}
                />
              </PresentationCol>
            </PresentationRow>
            {(!isPropertyManager || isOwnerEffective) &&
              selectedManager ===
                PROPERTY_MANAGEMENT_OPTIONS.PROPERTY_MANAGER && (
                <>
                  <PresentationRow className="mt-8 mb-8">
                    <PresentationCol
                      md={12}
                      xs={24}
                    >
                      <Text
                        as="h5"
                        style={{ fontWeight: 'bold' }}
                      >
                        Property Manager Information
                      </Text>
                    </PresentationCol>
                  </PresentationRow>
                  <PresentationRow>
                    <PresentationCol
                      md={12}
                      xs={24}
                    >
                      <PersonDetails
                        ref={propertyManagerForm}
                        formModel={propertyManagerFormModel}
                        formValues={propertyManagerFormValues}
                        propertyAddress={property?.propertyaddress}
                        validationModel={propertyManagerDynamicValidationModel}
                        setFormValues={setPropertyManagerFormValues}
                        hiddenFields={{
                          physicalAddress:
                            !propertyManagerData.optionalFields.physical_address
                              ?.required,
                        }}
                        optionalFields={
                          propertyManagerData.optionalFields as Record<
                            string,
                            'optional' | 'required'
                          >
                        }
                        disabled={!canChangePropertyManagerDetails}
                      />
                    </PresentationCol>
                  </PresentationRow>
                </>
              )}
          </>
        )}
        <PresentationRow
          className={`mb-16 ${registrant?.registrantType !== REGISTRANT_OPTIONS.PROPERTY_MANAGER && 'mt-24'}`}
        >
          <PresentationCol xs={24}>
            <Text
              as="h5"
              className="text-bold mb-8"
            >
              {localContactData.verbiage}
            </Text>
            <CustomHTML text={localContactData.description} />
          </PresentationCol>
        </PresentationRow>
        <PresentationRow>
          <PresentationCol
            md={12}
            xs={24}
          >
            <LocalContactRadioGroup
              error={localContactError}
              isOwner={registrantNotYetSet || isOwnerEffective}
              isTenant={isRegistrantTenant}
              isPropertyManager={isPropertyManager}
              localContactVerbiage={localContactData.verbiage ?? ''}
              onChange={handleChangeContact}
              propertyManager={selectedManager}
              selectedError={physicalAddressProximityError}
              value={selectedContact}
            />
          </PresentationCol>
        </PresentationRow>
        {selectedContact === LOCAL_CONTACT.OTHER && (
          <>
            <PresentationRow className="mt-24 mb-8">
              <PresentationCol
                md={12}
                xs={24}
              >
                <Text
                  as="h5"
                  style={{ fontWeight: 'bold' }}
                >
                  {localContactData.verbiage} Information
                </Text>
              </PresentationCol>
            </PresentationRow>
            <PresentationRow>
              <PresentationCol
                md={12}
                xs={24}
              >
                <PersonDetails
                  ref={ownerRepForm}
                  formModel={ownerRepFormModel}
                  formValues={ownerRepFormValues}
                  hiddenFields={{ physicalAddress: !physicalAddressRequired }}
                  optionalFields={
                    localContactData.optionalFields as Record<
                      string,
                      'optional' | 'required'
                    >
                  }
                  propertyAddress={property?.propertyaddress}
                  setFormValues={setOwnerRepFormValues}
                  validationModel={ownerRepDynamicValidationModel}
                />
              </PresentationCol>
            </PresentationRow>
          </>
        )}
        {hoaData.enabled && (
          <>
            <PresentationRow className="mt-24 mb-16">
              <PresentationCol xs={24}>
                <Text
                  as="h5"
                  className="text-bold mb-8"
                >
                  Home Owner&apos;s Association (HOA)
                </Text>
                <CustomHTML text={hoaData.description} />
              </PresentationCol>
            </PresentationRow>
            <PresentationRow>
              <PresentationCol
                md={12}
                xs={24}
              >
                <HoaRadioGroup
                  error={hoaError}
                  onChange={handleChangeHoa}
                  value={selectedHoa}
                />
              </PresentationCol>
            </PresentationRow>
            {selectedHoa === HOA_OPTIONS.YES && (
              <>
                <PresentationRow className="mt-8 mb-8">
                  <PresentationCol
                    md={12}
                    xs={24}
                  >
                    <Text
                      as="h5"
                      style={{ fontWeight: 'bold' }}
                    >
                      Home Owner&apos;s Association (HOA) Contact Information
                    </Text>
                  </PresentationCol>
                </PresentationRow>
                <PresentationRow>
                  <PresentationCol
                    md={12}
                    xs={24}
                  >
                    <PersonDetails
                      ref={hoaForm}
                      formModel={hoaFormModel}
                      formValues={hoaFormValues}
                      hiddenFields={{
                        physicalAddress:
                          !hoaData.optionalFields.physical_address?.required,
                      }}
                      optionalFields={
                        hoaData.optionalFields as Record<
                          string,
                          'optional' | 'required'
                        >
                      }
                      propertyAddress={property?.propertyaddress}
                      setFormValues={setHoaFormValues}
                      validationModel={hoaDynamicValidationModel}
                    />
                  </PresentationCol>
                </PresentationRow>
              </>
            )}
          </>
        )}
      </PresentationGrid>
    );
  }
);

ContactForm.displayName = 'ContactForm';
